/**
 * Writes.
 *
 * THE OFFLINE WRITE RULE, which is the single most counter-intuitive thing in
 * this codebase: **never `await` a Firestore write you want to survive being
 * offline, and never treat a pending promise as a failure.**
 *
 * `setDoc`/`addDoc` return a promise that resolves when the SERVER acknowledges.
 * Offline, that promise stays pending — for hours, until the phone finds a
 * network. The write itself is already durable in IndexedDB and already visible
 * through every listener the instant it is called. So:
 *
 *   await addDoc(...)               // hangs offline; a spinner that never stops
 *   toast('saved')                  // never runs offline
 *
 * is a save button that appears broken in exactly the place this app is used.
 * Fire the write, let the listener echo it back, and show sync state separately
 * (`usePendingWrites`). The promise is still worth attaching a `.catch()` to —
 * a permission-denied rejects immediately, online or off — but it is never worth
 * waiting for.
 *
 * The corollary: ids are generated client-side with `doc(collection(...))`, not
 * read out of an `addDoc` result, so the UI can navigate to the thing it just
 * created without a round trip.
 */
import {
  type Animal,
  type EventKind,
  type GrowthView,
  type LitterStatus,
  type MeasurementReading,
  mergePhotos,
  paths,
  type PhotoSession,
  type PhotoSubject,
  sessionOnDay,
  type Sex,
  stripUndefined,
  type UserProfile,
} from '@puppy-doctor/platform';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';

import { db } from '@/firebase.ts';

export type MeasurementEntryInput = {
  tenantId: string;
  litterId: string;
  /**
   * When the animals were actually weighed. Defaults to the client's clock, for
   * the same reason it is the client's clock and not `serverTimestamp()` — see
   * platform/src/types/index.ts. The wizard passes the moment the breeder picked
   * when the session is being typed in after the fact.
   */
  recordedAt?: number;
  /** One per animal weighed. An animal missing from it was not on the scale. */
  readings: readonly MeasurementReading[];
  recordedBy: string;
};

/**
 * A whole weighing session, written as ONE document.
 *
 * Returns the id it generated, synchronously in effect: the promise it resolves
 * with is the *local* write, which is complete before the caller's next line.
 *
 * One write where there used to be one per animal, and that is not only tidier
 * — offline it is one entry in the write queue instead of eight, so a litter
 * weighed in an outbuilding either syncs whole or does not sync at all. Half a
 * weighing arriving was a real state the old model could be in, and it drew a
 * chart with three puppies missing from Tuesday.
 */
export const recordMeasurementEntry = ({
  tenantId,
  litterId,
  recordedAt = Date.now(),
  readings,
  recordedBy,
}: MeasurementEntryInput): string => {
  const ref = doc(collection(db, paths.measurements(tenantId)));

  void setDoc(
    ref,
    stripUndefined({
      litterId,
      recordedAt,
      readings: readings.map((reading) => ({ ...reading })),
      recordedBy,
      createdAt: Date.now(),
      updatedBy: null,
      updatedAt: null,
    }),
  ).catch(reportWriteFailure);

  return ref.id;
};

/**
 * Correct a weighing already recorded — its numbers, its moment, or both.
 *
 * `recordedBy` is NOT rewritten, and that is the point of the audit: who ran the
 * weighing and who last corrected it are two different facts, and the old
 * per-animal model could only hold the second one. The rules enforce the same
 * thing from the other side — an update must leave `recordedBy` and `createdAt`
 * alone and must attribute `updatedBy` to the caller.
 *
 * A patch of exactly these four fields, never a whole-document write. See the
 * note on `toFirestore` in api/converter.ts.
 */
export const updateMeasurementEntry = ({
  tenantId,
  id,
  recordedAt,
  readings,
  updatedBy,
}: {
  tenantId: string;
  id: string;
  recordedAt: number;
  readings: readonly MeasurementReading[];
  updatedBy: string;
}): void => {
  void updateDoc(doc(db, paths.measurement(tenantId, id)), {
    recordedAt,
    readings: readings.map((reading) => ({ ...reading })),
    updatedBy,
    updatedAt: Date.now(),
  }).catch(reportWriteFailure);
};

/**
 * Forget a whole weighing.
 *
 * The whole session and not one animal's number, because the session is the
 * record: "we weighed them on Tuesday" is either true or it is not, and a
 * weighing with one puppy quietly removed from it is a worse record than no
 * weighing at all. Correcting one animal's number is what the wizard is for.
 */
export const deleteMeasurementEntry = (tenantId: string, id: string): void => {
  void deleteDoc(doc(db, paths.measurement(tenantId, id))).catch(reportWriteFailure);
};

export type RecordEventInput = {
  tenantId: string;
  offspringId: string;
  kind: EventKind;
  title: string;
  detail?: string;
  product?: string | null;
  dueAt?: number | null;
  recordedBy: string;
};

export const recordEvent = ({
  tenantId,
  offspringId,
  kind,
  title,
  detail = '',
  product = null,
  dueAt = null,
  recordedBy,
}: RecordEventInput): string => {
  const ref = doc(collection(db, paths.events(tenantId, offspringId)));

  void setDoc(
    ref,
    stripUndefined({
      kind,
      recordedAt: Date.now(),
      title,
      detail,
      product,
      dueAt,
      completedAt: null,
      recordedBy,
    }),
  ).catch(reportWriteFailure);

  return ref.id;
};

export const updateOffspring = (
  tenantId: string,
  offspringId: string,
  patch: Record<string, unknown>,
): void => {
  void updateDoc(doc(db, paths.offspringById(tenantId, offspringId)), stripUndefined(patch)).catch(
    reportWriteFailure,
  );
};

/** Creating a tenant is the one write whose id the server does not need to know first. */
export const createTenant = async (input: {
  name: string;
  breed: string;
  country: string | null;
  createdBy: string;
}): Promise<string> => {
  // The exception to the rule above, and the reason is worth stating: a tenant
  // is only usable once the onCreate function has written the owner membership,
  // which cannot happen offline. Awaiting here is honest — there genuinely is
  // nothing to show until the server has been heard from.
  const ref = await addDoc(collection(db, paths.tenants()), {
    ...input,
    createdAt: Date.now(),
  });
  return ref.id;
};

/**
 * The person's own display language, on their own profile document.
 *
 * The rules allow this: `users/{uid}` takes an update from its owner as long as
 * `tenantIds` is unchanged, and this touches one field. `updateDoc` rather than
 * a merging `setDoc` because the profile is written the moment the session is
 * first seen (see TenantProvider) — if it is missing here, that is a real
 * failure worth seeing in the console rather than a create the rules will deny
 * for a confusing reason.
 *
 * Not awaited, like every other write: the picker has already changed the
 * language locally, and this is the copy that follows the person to their other
 * device whenever the phone next finds a network.
 */
export const setProfileLocale = (uid: string, locale: UserProfile['locale']): void => {
  void updateDoc(doc(db, paths.user(uid)), { locale }).catch(reportWriteFailure);
};

/**
 * What this person is called, on their own profile document.
 *
 * Trimmed and stored as given, empty string included: empty is a real answer —
 * "I did not provide one" — and `displayNameOf` turns it into the local part of
 * the address wherever a name is drawn. Deleting the field instead would mean
 * the same thing in one more spelling.
 *
 * This does NOT change the name in any `members/{uid}` document already written.
 * Those are denormalised copies in a collection no client may write (see the
 * invariant in CLAUDE.md), so a rename shows up for this person everywhere the
 * profile is read and, in other people's copy of the people list, only from
 * their next join onwards.
 */
export const setProfileDisplayName = (uid: string, displayName: string): void => {
  void updateDoc(doc(db, paths.user(uid)), { displayName: displayName.trim() }).catch(
    reportWriteFailure,
  );
};

/**
 * Chart or table, on this person's own profile document.
 *
 * On the profile and not in `localStorage`, for the reason given on `GrowthView`
 * in the schema: it is a fact about the reader, so their tablet should open the
 * way their phone was left. Not awaited, like every other write — the toggle has
 * already switched locally, echoed back through the listener that reads the same
 * document. See `setProfileLocale`, which this is the sibling of.
 */
export const setProfileGrowthView = (uid: string, growthView: GrowthView): void => {
  void updateDoc(doc(db, paths.user(uid)), { growthView }).catch(reportWriteFailure);
};

// ---------------------------------------------------------------------------
// Litters and offspring
// ---------------------------------------------------------------------------

export type CreateLitterInput = {
  tenantId: string;
  name: string;
  status: LitterStatus;
  /** Date of birth, or the due date for an `incoming` litter. See the schema. */
  bornAt: number;
  sireId: string | null;
  damId: string | null;
  expectedCount: number | null;
  /** Free text, seeded from the tenant's own breed by the form. May be empty. */
  breed?: string;
  /**
   * What the dam weighed before this mating, in kilograms. Null until somebody
   * records it — the growth model asks for it rather than guessing, because it
   * is what decides whether a newborn counts as low birth weight.
   */
  damWeightKg?: number | null;
  notes?: string;
};

/**
 * Returns the id immediately, which is what makes the wizard's second step
 * possible offline: the litter exists locally and every listener has already
 * echoed it, so the puppies can be written against it before any server has
 * heard of either.
 */
export const createLitter = ({
  tenantId,
  name,
  status,
  bornAt,
  sireId,
  damId,
  expectedCount,
  breed = '',
  damWeightKg = null,
  notes = '',
}: CreateLitterInput): string => {
  const ref = doc(collection(db, paths.litters(tenantId)));

  void setDoc(
    ref,
    stripUndefined({
      name,
      status,
      bornAt,
      sireId,
      damId,
      expectedCount,
      breed,
      sizeGroup: null,
      damWeightKg,
      closedAt: null,
      notes,
    }),
  ).catch(reportWriteFailure);

  return ref.id;
};

export const updateLitter = (
  tenantId: string,
  litterId: string,
  patch: Record<string, unknown>,
): void => {
  void updateDoc(doc(db, paths.litter(tenantId, litterId)), stripUndefined(patch)).catch(
    reportWriteFailure,
  );
};

/**
 * `closedAt` is derived here rather than asked for, because it is the one thing
 * about a status change nobody wants to be prompted for. It is set on the way
 * into `historical` and cleared on the way out — a litter reopened by mistake
 * that kept its closing date would read as closed in every list that shows one.
 */
export const setLitterStatus = (
  tenantId: string,
  litterId: string,
  status: LitterStatus,
): void => {
  updateLitter(tenantId, litterId, {
    status,
    closedAt: status === 'historical' ? Date.now() : null,
  });
};

export type CreateOffspringInput = {
  tenantId: string;
  litterId: string;
  /** May be empty — a newborn is its collar before it is a name. */
  name: string;
  sex: Sex;
  markerColor: string;
  sequence: number;
  birthWeightGrams?: number | null;
  /** The profile picture, when one was taken while the puppy was described. */
  photoPath?: string | null;
  notes?: string;
};

export const createOffspring = ({
  tenantId,
  litterId,
  name,
  sex,
  markerColor,
  sequence,
  birthWeightGrams = null,
  photoPath = null,
  notes = '',
}: CreateOffspringInput): string => {
  const ref = doc(collection(db, paths.offspring(tenantId)));

  void setDoc(
    ref,
    stripUndefined({
      litterId,
      name: name.trim(),
      sex,
      markerColor,
      sequence,
      status: 'raising',
      birthWeightGrams,
      microchip: null,
      photoPath,
      notes,
    }),
  ).catch(reportWriteFailure);

  return ref.id;
};

export const deleteOffspring = (tenantId: string, offspringId: string): void => {
  void deleteDoc(doc(db, paths.offspringById(tenantId, offspringId))).catch(reportWriteFailure);
};

export type CreateAnimalInput = {
  tenantId: string;
  name: string;
  sex: Sex;
  /** Null for the outside stud whose birthday nobody at this end knows. */
  bornAt?: number | null;
  registration?: string | null;
  notes?: string;
};

/**
 * A sire or a dam, added from inside a litter form.
 *
 * A litter form is where a breeder first needs the parents to exist, and sending
 * them to a separate screen to create one — losing the half-filled litter form
 * on the way — is the reason this is a mutation and not a route.
 */
export const createAnimal = ({
  tenantId,
  name,
  sex,
  bornAt = null,
  registration = null,
  notes = '',
}: CreateAnimalInput): string => {
  const ref = doc(collection(db, paths.animals(tenantId)));

  void setDoc(
    ref,
    stripUndefined({
      name: name.trim(),
      sex,
      bornAt,
      registration,
      photoPath: null,
      notes,
    }),
  ).catch(reportWriteFailure);

  return ref.id;
};

/**
 * The dam, or the sire, as the litter form spells them: by name.
 *
 * A litter stores `damId` and `sireId`, but the person filling the form is
 * thinking of a name and not of a record — so the form asks for the name and
 * this turns it into an id, creating the animal when the kennel has not met it
 * yet. `createAnimal` returns its id from the local write, so a parent invented
 * here is usable on the very next line, offline included.
 *
 * Matched case-insensitively and within one sex, so "maja" typed on the second
 * litter finds the "Maja" typed on the first instead of quietly making a second
 * bitch of the same name. Accents still separate — `Zośka` and `Zoska` are two
 * names, and the one the breeder typed is the one they meant.
 *
 * An empty name is a real answer: a rescued bitch's sire is genuinely unknown,
 * and it becomes `null` rather than an animal called "".
 */
export const animalIdByName = ({
  tenantId,
  name,
  sex,
  animals,
}: {
  tenantId: string;
  name: string;
  sex: Sex;
  animals: readonly Animal[];
}): string | null => {
  const trimmed = name.trim();
  if (trimmed === '') return null;

  const known = animals.find(
    (animal) =>
      animal.sex === sex &&
      animal.name.localeCompare(trimmed, undefined, { sensitivity: 'accent' }) === 0,
  );

  return known?.id ?? createAnimal({ tenantId, name: trimmed, sex });
};

// ---------------------------------------------------------------------------
// Photographs
// ---------------------------------------------------------------------------

export type PhotoSessionInput = {
  tenantId: string;
  litterId: string;
  /**
   * When the photographs were taken. Defaults to the client's clock, for the
   * same reason `MeasurementEntryInput.recordedAt` does — see
   * platform/src/types/index.ts. The wizard passes the day the breeder picked
   * when a session is being uploaded after the fact.
   */
  takenAt?: number;
  /** Photographs of the litter as a whole, of nobody in particular. */
  litterPaths: readonly string[];
  /** One entry per animal photographed. An animal missing from it was not shot. */
  subjects: readonly PhotoSubject[];
  recordedBy: string;
};

/**
 * A whole afternoon of photographs, written as ONE document.
 *
 * The files are already in Storage by the time this runs — that upload is the
 * one part of the app that genuinely needs a network, and it is awaited where
 * this is not. This records where they landed, whose they are and what day it
 * was, which is the half that has to survive being offline: one entry in the
 * write queue instead of thirty, so a session shot in an outbuilding either
 * syncs whole or does not sync at all.
 *
 * Returns the id it generated, synchronously in effect — the promise resolves
 * against the *local* write, which is complete before the caller's next line.
 */
export const recordPhotoSession = ({
  tenantId,
  litterId,
  takenAt = Date.now(),
  litterPaths,
  subjects,
  recordedBy,
}: PhotoSessionInput): string => {
  const ref = doc(collection(db, paths.photoSessions(tenantId)));

  void setDoc(
    ref,
    stripUndefined({
      litterId,
      takenAt,
      paths: [...litterPaths],
      subjects: subjects.map((subject) => ({
        offspringId: subject.offspringId,
        paths: [...subject.paths],
      })),
      recordedBy,
      createdAt: Date.now(),
      updatedBy: null,
      updatedAt: null,
    }),
  ).catch(reportWriteFailure);

  return ref.id;
};

/**
 * Correct a photographing already recorded — its day, its pictures, or both.
 *
 * `recordedBy` is NOT rewritten, for the reason `updateMeasurementEntry` gives:
 * who took the photographs and who last changed the record are two different
 * facts. The rules enforce it from the other side — an update must leave
 * `recordedBy`, `createdAt` and `litterId` alone and must attribute `updatedBy`
 * to the caller.
 *
 * A patch of exactly these five fields, never a whole-document write. See the
 * note on `toFirestore` in api/converter.ts.
 *
 * The files a correction drops are left in Storage, referenced by nothing — the
 * same trade the wizard's Discard already makes. Deleting them is a second
 * failure mode, and a delete that fails offline is a delete that never happens.
 */
export const updatePhotoSession = ({
  tenantId,
  id,
  takenAt,
  litterPaths,
  subjects,
  updatedBy,
}: {
  tenantId: string;
  id: string;
  takenAt: number;
  litterPaths: readonly string[];
  subjects: readonly PhotoSubject[];
  updatedBy: string;
}): void => {
  void updateDoc(doc(db, paths.photoSession(tenantId, id)), {
    takenAt,
    paths: [...litterPaths],
    subjects: subjects.map((subject) => ({
      offspringId: subject.offspringId,
      paths: [...subject.paths],
    })),
    updatedBy,
    updatedAt: Date.now(),
  }).catch(reportWriteFailure);
};

/**
 * What the photo wizard saves — and the one place the ONE-SESSION-PER-DAY rule
 * is applied.
 *
 * A day is a record. A breeder who photographs the litter before breakfast and
 * again at four has not had two photographings; they have had one, in two
 * sittings, and the archive is read by the day — so the second sitting is folded
 * into the record of that day instead of standing beside it as a second card
 * saying "Day 12" with the same puppies in different piles.
 *
 * THE THREE OUTCOMES, and which primitive each is:
 *
 *   nothing on that day yet    a new document, or a patch of the one being
 *                              corrected — `recordPhotoSession` /
 *                              `updatePhotoSession`, unchanged.
 *   a session already there    its pictures and these, merged onto IT: it keeps
 *                              `recordedBy` and `createdAt`, and this caller
 *                              signs `updatedBy`. See `sessionOnDay`.
 *   …and this was a correction moved onto that day. The merge lands first and
 *                              the document being corrected is then deleted, so
 *                              a queue interrupted between the two writes loses
 *                              nothing: the pictures are already in the survivor
 *                              before anything is dropped.
 *
 * IT DECIDES FROM `sessions`, WHICH IS THE LIVE QUERY the screen is already
 * holding — no read here, so this works with the radio off exactly like every
 * other write in this file. Offline the listener's copy is the cache's, which is
 * the same answer the server would give for a litter only this kennel writes to.
 * Two phones adding the same day while both are offline is the one case this
 * cannot see, and it resolves the way it always did: two documents for one day,
 * which the timeline draws as two cards until somebody corrects one onto the
 * other. It is not a correctness problem; no picture is lost either way.
 */
export const savePhotoSession = ({
  tenantId,
  litterId,
  sessions,
  editing,
  takenAt,
  litterPaths,
  subjects,
  by,
}: {
  tenantId: string;
  litterId: string;
  /** Every photographing this litter has, from the live query. See above. */
  sessions: readonly PhotoSession[];
  /** The photographing being corrected, or null when this is a new one. */
  editing: PhotoSession | null;
  takenAt: number;
  litterPaths: readonly string[];
  subjects: readonly PhotoSubject[];
  /** The signed-in person: `recordedBy` on a new session, `updatedBy` on any patch. */
  by: string;
}): void => {
  const standing = sessionOnDay(sessions, takenAt, editing?.id);

  if (standing === null) {
    if (editing === null) {
      recordPhotoSession({ tenantId, litterId, takenAt, litterPaths, subjects, recordedBy: by });
    } else {
      updatePhotoSession({ tenantId, id: editing.id, takenAt, litterPaths, subjects, updatedBy: by });
    }
    return;
  }

  // The standing record first: it is the older half of the day.
  const merged = mergePhotos([standing, { paths: litterPaths, subjects }]);

  updatePhotoSession({
    tenantId,
    id: standing.id,
    takenAt,
    litterPaths: merged.paths,
    subjects: merged.subjects,
    updatedBy: by,
  });

  if (editing !== null) deletePhotoSession(tenantId, editing.id);
};

/**
 * Forget a whole photographing.
 *
 * The session and not one picture, for the reason `deleteMeasurementEntry`
 * gives: the session is the record. Removing one photograph from one is what
 * the wizard is for.
 *
 * The files stay in Storage. A delete that fails offline is a delete that never
 * happens, and a second failure mode buys nothing a breeder can see — the paths
 * are now referenced by nothing.
 */
export const deletePhotoSession = (tenantId: string, id: string): void => {
  void deleteDoc(doc(db, paths.photoSession(tenantId, id))).catch(reportWriteFailure);
};

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

/**
 * One thing to do to a litter, written down.
 *
 * `dueAt` is local midnight of the day it is due — the caller normalises it,
 * because the caller is the one holding the day the reader picked. See `Task` in
 * platform/src/types/index.ts and `startOfDay` in lib/dates.ts.
 *
 * Returns the id it generated, so the section can leave the new row open for a
 * correction without waiting for a round trip that offline never comes.
 */
export const createTask = ({
  tenantId,
  litterId,
  text,
  dueAt,
  createdBy,
}: {
  tenantId: string;
  litterId: string;
  text: string;
  dueAt: number;
  createdBy: string;
}): string => {
  const ref = doc(collection(db, paths.tasks(tenantId)));

  void setDoc(
    ref,
    stripUndefined({
      litterId,
      text,
      dueAt,
      doneAt: null,
      doneBy: null,
      createdBy,
      createdAt: Date.now(),
    }),
  ).catch(reportWriteFailure);

  return ref.id;
};

/**
 * Change what a task says, or when it is due.
 *
 * A patch of exactly these two fields. `createdBy` and `createdAt` are frozen by
 * the rules, and `doneAt`/`doneBy` are left alone deliberately: correcting the
 * wording of a job somebody has already finished must not quietly reopen it.
 */
export const updateTask = ({
  tenantId,
  id,
  text,
  dueAt,
}: {
  tenantId: string;
  id: string;
  text: string;
  dueAt: number;
}): void => {
  void updateDoc(doc(db, paths.task(tenantId, id)), { text, dueAt }).catch(reportWriteFailure);
};

/**
 * Tick a task off, or put it back.
 *
 * The moment and the person move together — that is what the security rule
 * insists on, so that nobody can sign somebody else's name against a job — and
 * untick clears both. `Date.now()` rather than `serverTimestamp()`, because the
 * history is ordered by it and an unresolved timestamp would sort a task ticked
 * off in the whelping room to the bottom of a list it should be at the top of.
 */
export const setTaskDone = ({
  tenantId,
  id,
  done,
  uid,
}: {
  tenantId: string;
  id: string;
  done: boolean;
  uid: string;
}): void => {
  void updateDoc(doc(db, paths.task(tenantId, id)), {
    doneAt: done ? Date.now() : null,
    doneBy: done ? uid : null,
  }).catch(reportWriteFailure);
};

/**
 * Forget a task.
 *
 * Deleted outright rather than archived, and unlike a weighing it needs no
 * higher permission than writing one: a task is a note about something that has
 * not happened, and striking one out is as ordinary as writing it down.
 */
export const deleteTask = (tenantId: string, id: string): void => {
  void deleteDoc(doc(db, paths.task(tenantId, id))).catch(reportWriteFailure);
};

/**
 * A rejected write is not a network problem — offline writes do not reject.
 * It is permission-denied, a failed precondition, or a document too large, and
 * every one of those means the data the user entered is not going to be saved.
 * Wire this to whatever the app uses for error reporting; silence is the one
 * unacceptable option.
 */
const reportWriteFailure = (error: unknown): void => {
  console.error('[puppy-doctor] write rejected', error);
};
